import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { Checkbox, Icon, Modal } from 'semantic-ui-react';
import axios from 'axios';
import Dropzone from 'react-dropzone';
import { convertToBase64 } from 'lib/PublicFunctions';
import produce from 'immer';
import TextField from 'components/ui/TextField';
import TextArea from 'components/forms/ui/TextArea';
import Button from 'components/ui/Button';
import ImportedFileTag from 'components/new-order-entry/Documents/ImportedFileTag';
import { Link } from 'react-router-dom';
import { PopOut } from 'components/ui/icons';
import moment from 'moment';
import VprUasOverride from './VprUasOverride';

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
`;
const Close = styled.button`
  display: flex;
  justify-content: flex-end;
  padding: 15px;
  background-color: #fff;
  border: none;
  cursor: pointer;
`;
const ActualContent = styled.div`
  padding: 0 35px;
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 9px;
  margin-top: 40px;
  justify-content: flex-end;
  position: relative;
`;

const DocumentOptions = styled.div`
  border: 1.4px dashed #e5e5ea;
  padding: 8px;
  border-radius: 4px;
  width: 376px;
`;
const UploadButton = styled.button`
  background-color: #ff5252;
  border-radius: 5px;
  color: #fff;
  height: 32px;
  padding: 5px 16px;
  margin-right: 16px;
  font-weight: bold;
`;
const DragDrop = styled.span`
  padding-left: 16px;
  border-left: 2px solid #e5e5ea;
`;

const IconButton = styled.button`
  background-color: #f7f7f9;
  border-radius: 5.87px;
  height: 40px;
  width: 40px;
  min-width: 40px;
  color: #8e8e93;
`;
export const Overlay = styled.div`
  background-color: rgba(253, 253, 253, 0.65);
  bottom: 0px;
  height: 100%;
  left: 0px;
  position: absolute;
  right: 0px;
  top: 0px;
  width: 100%;
  z-index: 50;
`;
const SavedModal = styled.div`
  border-radius: 5px;
  background-color: #f5f5f5;
  width: 270px;
  height: 48px;
  color: #ff867f;
  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  top: 0px;
  right: 0px;
`;
interface Props {
  open: boolean;
  onClose: any;
  municipalityId: number;
}
interface File {
  base64?: string;
  actualFileName?: string;
  lastDateUploaded?: string;
  docId?: number;
}
interface VprUasInfo {
  id: number;
  municipalityId: number;
  vprNotApplicable: boolean;
  uasNotApplicable: boolean;
  opraEmail: string;
  opraLink: string;
  notes: string;
  vprLastUpdatedFiles: File[];
  uasLastUpdatedFiles: File[];
  vprFrequencyUpdate: string;
  uasFrequencyUpdate: string;
  vprUploadedBy?: string;
  uasUploadedBy?: string;
  uasLastRequested?: string;
  vprLastRequested?: string;
  vprFrontMessageId?: string;
  uasFrontMessageId?: string;
}

const initialVprUasInfo: VprUasInfo = {
  id: null,
  vprNotApplicable: false,
  uasNotApplicable: false,
  opraEmail: '',
  opraLink: '',
  notes: '',
  vprLastUpdatedFiles: [],
  uasLastUpdatedFiles: [],
  vprFrequencyUpdate: '',
  uasFrequencyUpdate: '',
  municipalityId: 0,
};
const VprUasModal = (props: Props) => {
  const [vprUasInfo, setVprUasInfo] = useState<VprUasInfo>(initialVprUasInfo);
  const [vprOverlayOpen, setVprOverlayOpen] = useState(false);
  const [uasOverlayOpen, setUasOverlayOpen] = useState(false);
  const [loader, setLoader] = useState(false);
  const [savedPopup, setSavedPopup] = useState(false);
  const [openVprOverride, setOpenVprOverride] = useState(false);
  const [openUasOverride, setOpenUasOverride] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);

  const onDropVpr = async (newFiles: any) => {
    const arr: File[] = [];
    for (let file of newFiles) {
      const base64 = await convertToBase64(file);
      const tempFile: File = {
        base64,
        actualFileName: file.name,
      };
      arr.push(tempFile);
    }
    const newArray = produce(vprUasInfo.vprLastUpdatedFiles, draft => {
      for (let file of arr) {
        draft.push(file);
      }
    });
    changeVprUasInfo('vprLastUpdatedFiles', newArray);
  };
  const onDropUas = async (newFiles: any) => {
    const arr: File[] = [];
    for (let file of newFiles) {
      const base64 = await convertToBase64(file);
      const tempFile: File = {
        base64,
        actualFileName: file.name,
      };
      arr.push(tempFile);
    }
    const newArray = produce(vprUasInfo.uasLastUpdatedFiles, draft => {
      for (let file of arr) {
        draft.push(file);
      }
    });
    changeVprUasInfo('uasLastUpdatedFiles', newArray);
  };
 
  
  const onRemoveVprFileClick = (index: any) => {
    const newVprFiles = vprUasInfo.vprLastUpdatedFiles.filter((f, i) => i !== index);
    changeVprUasInfo('vprLastUpdatedFiles', newVprFiles);
  };
  const onRemoveUasFileClick = (index: any) => {
    const newUasFiles = vprUasInfo.uasLastUpdatedFiles.filter((f, i) => i !== index);
    changeVprUasInfo('uasLastUpdatedFiles', newUasFiles);
  };
  const changeVprUasInfo = (label: keyof Partial<VprUasInfo>, value: any) => {
    const newVprUasInfo = produce(vprUasInfo, draft => {
      (draft[label] as keyof VprUasInfo) = value;
    });
    setVprUasInfo(newVprUasInfo);
  };
  const getVprUasInfo = async () => {
    const { data } = await axios.get(`/api/vpruas/GetVprUas/${props.municipalityId}`);
    if (data) {
      setVprUasInfo(data);
    }
  };

  const onSubmit = async () => {
    setLoader(true);
    await axios.post(`/api/vpruas/AddVprUasInfo`, {
      ...vprUasInfo,
      municipalityId: props.municipalityId,
    });
    setLoader(false);
    setSavedPopup(true);
    setTimeout(() => {
      setSavedPopup(false);
      props.onClose();
    }, 1000);
  };
  useEffect(() => {
    if (props.municipalityId) {
      getVprUasInfo();
    }
  }, [props.municipalityId]);

  const onSendOpra = async (programType: 'VPR' | 'UAS', overrideText: string) => {
    setSubmitLoading(true);
    const subroute = programType === 'VPR' ? 'sendVprOpra' : 'sendUasOpra';

    const { data } = await axios.post(`/api/vpruas/${subroute}/${props.municipalityId}`, {
      overrideText,
    });
    if (data.errorMessage) {
      alert(data.errorMessage);
    }
    await getVprUasInfo();
    if (programType === 'UAS') {
      setOpenUasOverride(false);
    } else {
      setOpenVprOverride(false);
    }
    setSubmitLoading(false);
  };

  useEffect(() => {
    if (vprUasInfo.vprNotApplicable) {
      setVprOverlayOpen(true);
    } else {
      setVprOverlayOpen(false);
    }
  }, [vprUasInfo?.vprNotApplicable]);

  useEffect(() => {
    if (vprUasInfo.uasNotApplicable) {
      setUasOverlayOpen(true);
    } else {
      setUasOverlayOpen(false);
    }
  }, [vprUasInfo?.uasNotApplicable]);

  return (
    <Modal
      open={props.open}
      style={{ minHeight: 780, width: 650, zIndex: 500, paddingBottom: 24 }}
      onClose={props.onClose}
    >
      <ModalContent>
        <Close onClick={props.onClose}>
          <Icon name="close" />
        </Close>
        <ActualContent>
          <div style={{ borderBottom: '1px solid #e5e5ea', paddingBottom: 24, paddingTop: 16 }}>
            <div style={{ display: 'flex', marginBottom: 16, justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <div style={{ fontSize: 24, fontWeight: 'bold', marginRight: 24 }}>VPR</div>
                <Checkbox
                  label="N/A"
                  checked={vprUasInfo?.vprNotApplicable}
                  onChange={(e, { checked }) => {
                    {
                      changeVprUasInfo('vprNotApplicable', checked);
                    }
                  }}
                />
                {!vprUasInfo.vprNotApplicable && (
                  <Button
                    black
                    overRidestyle={{ height: 24, marginLeft: 16 }}
                    onClick={() => setOpenVprOverride(true)}
                  >
                    Send Vpr Opra
                  </Button>
                )}
                {vprUasInfo.vprLastRequested && (
                  <div style={{ marginLeft: 8 }}>
                    Last sent: {moment(vprUasInfo.vprLastRequested).format('MM/DD/YY')}
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <div>Every</div>
                <TextField
                  onChange={e => changeVprUasInfo('vprFrequencyUpdate', e.target.value)}
                  value={vprUasInfo?.vprFrequencyUpdate}
                  width={'40px'}
                  styles={{ marginLeft: 8 }}
                  height={24}
                />
                <div style={{ marginLeft: 8 }}>days</div>
              </div>
            </div>
            {openVprOverride && (
              <VprUasOverride
                programType="VPR"
                closeOverride={() => setOpenVprOverride(false)}
                onSendOpra={onSendOpra}
                submitting={submitLoading}
              />
            )}
            <div style={{ position: 'relative' }}>
              {vprOverlayOpen && <Overlay />}
              <div style={{ marginBottom: 16, paddingTop: 16 }}>
                <label style={{ display: 'block', marginBottom: 4, fontSize: 14 }}>
                 VPR Front Message ID
                </label>
                <input
                  type="text"
                  value={vprUasInfo.vprFrontMessageId || ''}
                  onChange={e => changeVprUasInfo('vprFrontMessageId', e.target.value)}
                  placeholder="Enter Front Message ID"
                  style={{
                    padding: '8px 12px',
                    border: '1px solid #e5e5ea',
                    borderRadius: 4,
                    width: 376,
                    fontSize: 14,
                  }}
                />
              </div>
              <Dropzone onDrop={onDropVpr}>
                {({ getRootProps, getInputProps, isDragActive }) => {
                  return (
                    <DocumentOptions>
                      <div {...getRootProps()}>
                        <input {...getInputProps()} />
                        <UploadButton>Upload Document</UploadButton>
                        <DragDrop>
                          {isDragActive ? <span>drop here</span> : <span>or drag and drop files</span>}
                        </DragDrop>
                      </div>
                    </DocumentOptions>
                  );
                }}
              </Dropzone>
              <div
                style={{
                  display: 'flex',
                  //alignItems: 'center',
                  position: 'relative',
                  flexDirection: 'column',
                }}
              >
                {vprUasInfo.vprLastUpdatedFiles?.map((file, index) => {
                  return (
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        position: 'relative',
                        gap: 16,
                      }}
                    >
                      <div key={file.actualFileName}>
                        <ImportedFileTag
                          fileNameToDisplay={file.actualFileName}
                          onRemoveClick={e => {
                            e.stopPropagation();
                            onRemoveVprFileClick(index);
                          }}
                          changeCustomFileName={() => {}}
                          noMargin={true}
                          minWidth={'376px'}
                          noWidth={true}
                        />
                      </div>
                      {file.lastDateUploaded && (
                        <div>
                          <div>Last Updated </div>
                          <div>{file.lastDateUploaded}</div>
                        </div>
                      )}
                      {file.docId && (
                        <Link to={`/api/documents/getDocument/${file.docId}`} target="_blank" replace>
                          <IconButton>
                            <PopOut color="#FF5252" />
                          </IconButton>
                        </Link>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', marginBottom: 16, marginTop: 24, justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <div style={{ fontSize: 24, fontWeight: 'bold', marginRight: 24 }}>UAS</div>
              <Checkbox
                label="N/A"
                checked={vprUasInfo?.uasNotApplicable}
                onChange={(e, { checked }) => {
                  {
                    changeVprUasInfo('uasNotApplicable', checked);
                  }
                }}
              />
              {!vprUasInfo.uasNotApplicable && (
                <Button
                  black
                  overRidestyle={{ height: 24, marginLeft: 16 }}
                  onClick={() => setOpenUasOverride(true)}
                >
                  Send Uas Opra
                </Button>
              )}
              {vprUasInfo.uasLastRequested && (
                <div style={{ marginLeft: 8 }}>
                  Last sent: {moment(vprUasInfo.uasLastRequested).format('MM/DD/YY')}
                </div>
              )}
            </div>

            <div style={{ display: 'flex', alignItems: 'center' }}>
              <div>Every</div>
              <TextField
                onChange={e => changeVprUasInfo('uasFrequencyUpdate', e.target.value)}
                value={vprUasInfo?.uasFrequencyUpdate}
                width={'40px'}
                styles={{ marginLeft: 8 }}
                height={24}
              />
              <div style={{ marginLeft: 8 }}>days</div>
            </div>
          </div>
          {openUasOverride && (
            <VprUasOverride
              programType="UAS"
              closeOverride={() => setOpenUasOverride(false)}
              onSendOpra={onSendOpra}
              submitting={submitLoading}
            />
          )}
          <div style={{ position: 'relative' }}>
            {uasOverlayOpen && <Overlay />}
            <div style={{ marginBottom: 16, paddingTop: 16 }}>
              <label style={{ display: 'block', marginBottom: 4, fontSize: 14 }}>
               UAS Front Message ID
              </label>
              <input
                type="text"
                value={vprUasInfo.uasFrontMessageId || ''}
                onChange={e => changeVprUasInfo('uasFrontMessageId', e.target.value)}
                placeholder="Enter Front Message ID"
                style={{
                  padding: '8px 12px',
                  border: '1px solid #e5e5ea',
                  borderRadius: 4,
                  width: 376,
                  fontSize: 14,
                }}
              />
            </div>
            <Dropzone onDrop={onDropUas}>
              {({ getRootProps, getInputProps, isDragActive }) => {
                return (
                  <DocumentOptions>
                    <div {...getRootProps()}>
                      <input {...getInputProps()} />
                      <UploadButton>Upload Document</UploadButton>
                      <DragDrop>
                        {isDragActive ? <span>drop here</span> : <span>or drag and drop files</span>}
                      </DragDrop>
                    </div>
                  </DocumentOptions>
                );
              }}
            </Dropzone>
            <div
              style={{
                display: 'flex',
                paddingBottom: 24,
                position: 'relative',
                flexDirection: 'column',
              }}
            >
              {vprUasInfo.uasLastUpdatedFiles?.map((file, index) => {
                return (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      position: 'relative',
                      gap: 8,
                    }}
                  >
                    <div key={file.actualFileName}>
                      <ImportedFileTag
                        fileNameToDisplay={file.actualFileName}
                        onRemoveClick={e => {
                          e.stopPropagation();
                          onRemoveUasFileClick(index);
                        }}
                        changeCustomFileName={() => {}}
                        noMargin={true}
                        minWidth={'376px'}
                        noWidth={true}
                      />
                    </div>
                    {file.lastDateUploaded && (
                      <div>
                        <div>Last Updated </div>
                        <div>{file.lastDateUploaded}</div>
                      </div>
                    )}
                    {file.docId && (
                      <Link to={`/api/documents/getDocument/${file.docId}`} target="_blank" replace>
                        <IconButton>
                          <PopOut color="#FF5252" />
                        </IconButton>
                      </Link>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
          <div style={{ borderTop: '1px solid #e5e5ea', paddingTop: 16 }}>
            <div style={{ display: 'flex' }}>
              <TextField
                onChange={e => changeVprUasInfo('opraEmail', e.target.value)}
                value={vprUasInfo?.opraEmail}
                width={'245px'}
                label="Opra Email Address"
              />
              <div style={{ marginLeft: 8 }}>
                <TextField
                  onChange={e => changeVprUasInfo('opraLink', e.target.value)}
                  value={vprUasInfo?.opraLink}
                  width={'245px'}
                  label="Opra Link"
                />
              </div>
            </div>
            <div style={{ marginTop: 16 }}>
              <TextArea
                value={vprUasInfo.notes || ''}
                onChange={(e: any) => changeVprUasInfo('notes', e.target.value)}
                styles={{
                  marginTop: 8,
                  width: 498,
                  height: 54,
                  borderRadius: 5,
                  resize: 'vertical',
                  ':focus': {
                    outline: 'none',
                  },
                }}
                label="Notes:"
                placeholder=""
                maxLength={52000}
              />
            </div>
          </div>
          <ButtonContainer>
            <Button secondary onClick={props.onClose}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                onSubmit();
              }}
              loadingStatus={loader}
            >
              Submit
            </Button>
            {savedPopup && <SavedModal>Successfully Saved</SavedModal>}
          </ButtonContainer>
        </ActualContent>
      </ModalContent>
    </Modal>
  );
};

export default VprUasModal;
